import { Group, Stack } from "@mantine/core"
import { InformationItem, CopyButton } from "@tfg/ui"
import { useMemo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import type { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { IconRawMaterial } from "~/components/icons/activities/IconRawMaterial"
import { useTraceabilityOperations } from "~/pages/SupplierPortal/TraceabilityRequests/context/useTraceabilityOperations"
import { useGetTranslation } from "~/utils/translations.ts"

import { isComponentItem, isMaterialItem, isProductItem } from "../utils"

import { MaterialComposition } from "./MaterialComposition"

interface ItemInformationProps {
  item: TraceabilityResponses.TraceabilityRequestItem
  customLabel?: ReactNode
  showTitle?: boolean
}

export const ItemTypeInformation = ({
  item,
  customLabel,
  showTitle = true,
}: ItemInformationProps) => {
  const { t } = useTranslation(["traceability"])
  const label = useGetTranslation(item.itemInformation.type)

  if (isProductItem(item)) {
    return (
      <InformationItem
        title={showTitle ? t("item.product") : undefined}
        value={customLabel ?? label}
      />
    )
  }

  if (isComponentItem(item)) {
    return (
      <InformationItem
        title={showTitle ? t("item.component") : undefined}
        helpHover={<MaterialComposition item={item} />}
        icon={<IconRawMaterial size={16} />}
        value={customLabel ?? label}
      />
    )
  }

  if (isMaterialItem(item)) {
    return (
      <InformationItem
        title={showTitle ? t("item.material_one") : undefined}
        value={customLabel ?? label}
      />
    )
  }

  return null
}

export function findParentReferenceInTree(
  item: TraceabilityResponses.TraceabilityRequestItem,
  targetItemId: number,
  parentReference: string | undefined
): string | undefined {
  if (item.id === targetItemId) {
    return parentReference
  }

  if (!("children" in item) || item.children.length === 0) {
    return undefined
  }

  for (const child of item.children) {
    const childParentReference = findParentReferenceInTree(
      child,
      targetItemId,
      item.reference
    )
    if (childParentReference !== undefined) {
      return childParentReference
    }
  }

  return undefined
}

export const OperationItemInformation = () => {
  const { t } = useTranslation(["traceability"])
  const { isAdmin, operationItem, request } = useTraceabilityOperations()
  const parentReference = useMemo(
    () =>
      findParentReferenceInTree(
        request.original.item,
        operationItem.item.id,
        undefined
      ),
    [operationItem.item.id, request.original.item]
  )

  return (
    <Stack gap="xs" w="100%">
      {isAdmin && (
        <Group>
          {operationItem.operation?.id && (
            <CopyButton
              label="Operation Id"
              size="xs"
              admin={isAdmin}
              copyableText={operationItem.operation.id.toString()}
            />
          )}
        </Group>
      )}
      <ItemTypeInformation item={operationItem.item} />
      {parentReference && (
        <InformationItem title={t("item.partOf")} value={parentReference} />
      )}
    </Stack>
  )
}
